package com.arena_web.ui.sections.eventdetail

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.EventDetailDto
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import kotlin.js.Date

@Composable
fun CompletedMatchBanner(
    event: EventDetailDto,
    modifier: Modifier = Modifier,
    fillHeight: Boolean = false,
) {
    val t = LocalAppStrings.current
    val parsed = Date(event.dateTime)
    val months = arrayOf(
        t.eventMonthJanuary, t.eventMonthFebruary, t.eventMonthMarch, t.eventMonthApril,
        t.eventMonthMay, t.eventMonthJune, t.eventMonthJuly, t.eventMonthAugust,
        t.eventMonthSeptember, t.eventMonthOctober, t.eventMonthNovember, t.eventMonthDecember
    )
    val dateStr = "${parsed.getDate()} ${months[parsed.getMonth()]}"
    val timeStr = "${parsed.getHours().toString().padStart(2, '0')}:${
        parsed.getMinutes().toString().padStart(2, '0')
    }"

    // Title comes from the event's own `title` field — the name the
    // creator picked in the Create Event flow. Team-vs-team string was
    // the old derivation; flipped per user direction so completed events
    // show the same headline they were created under.
    val eventTitle = event.title.uppercase()

    // Players per team (format field)
    val playersPerTeam = event.format ?: 0
    val totalPlayers = event.participants.count { it.status.uppercase() == "CONFIRMED" }

    // Full-width banner with event image background. When `fillHeight` is
    // true (non-Pro completed view per Figma 7902:347147) the banner expands
    // to fill the available column height; otherwise it sticks to the 404 px
    // hero from the Pro layout.
    Box(
        modifier = modifier
            .fillMaxWidth()
            .then(if (fillHeight) Modifier.fillMaxHeight() else Modifier.sHeight(404))
            .sBorderRadius(if (fillHeight) 28 else 16)
            .overflow(Overflow.Hidden)
            .position(Position.Relative)
            .styleModifier {
                property(
                    "background",
                    "url('${event.imageUrl ?: "/icons/event_football_field.png"}') center/cover no-repeat"
                )
            }
    ) {

        // Left-side dark gradient overlay
        Box(
            modifier = Modifier
                .sWidth(396)
                .fillMaxHeight()
                .position(Position.Absolute)
                .top(0.px).left(0.px)
                .styleModifier {
                    property("background-image", AppGradients.OverlayPartialGradient)
                }
        )

        // Info card — semi-transparent overlay. Width matches MatchInfoBanner
        // (538 px) so the registration and completed states share the same
        // hero footprint. Gradient outline is painted by an absolute overlay
        // (mask-composite trick) per project convention.
        Column(
            modifier = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property("left", s(22))
                    property("top", s(60))
                    property("width", s(538))
                    property("background", "rgba(2, 4, 11, 0.4)")
                    property("backdrop-filter", "blur(4px)")
                }
                .sBorderRadius(22)
                .sPadding(22, 24)
                .sGap(40)
        ) {
            Box(
                modifier = Modifier.styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "0.3px solid transparent")
                    property("border-radius", s(22))
                    property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                }
            )
            // Title row — event title on the left. The PRO+ pill plus the
            // Completed status pill are stacked in an *absolutely-positioned*
            // column at the row's top-right so the badge stack doesn't
            // contribute its (taller) height to the row, which would push
            // the date/location content down and grow the info card past
            // its previous footprint. Right-padding on the title text
            // reserves room for the badges so the headline doesn't slide
            // under them. PRO+ sits flush with the title baseline; Completed
            // chains directly below it (`sGap(10)`).
            Box(
                modifier = Modifier.fillMaxWidth().position(Position.Relative),
            ) {
                SpanText(
                    eventTitle,
                    modifier = Modifier
                        .fillMaxWidth()
                        .sPaddingRight(180)
                        .sFontSize(26)
                        .fontWeight(FontWeight.SemiBold)
                        .sLineHeight(30)
                        .color(AppColors.Text.Secondary)
                        .styleModifier { property("letter-spacing", s(0.26)) }
                )
                Column(
                    modifier = Modifier
                        .sGap(10)
                        .position(Position.Absolute)
                        .styleModifier {
                            property("top", "0")
                            property("right", "0")
                        },
                    horizontalAlignment = Alignment.End,
                ) {
                    if (event.isPro) {
                        ProStatusBadge(sportColors = LocalSportColors.current)
                    }
                    CompletedStatusBadge()
                }
            }

            // Main information
            Column(modifier = Modifier.sGap(24)) {
                // Date, time, location
                Column(modifier = Modifier.sGap(8)) {
                    // Row 1: Time + Date
                    Row(modifier = Modifier.sGap(18)) {
                        Row(
                            modifier = Modifier.sGap(6),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(src = AppIcons.Time)
                            SpanText(
                                timeStr,
                                modifier = Modifier
                                    .sFontSize(18)
                                    .fontWeight(FontWeight.Medium)
                                    .color(AppColors.Text.Secondary)
                                    .styleModifier { property("letter-spacing", s(0.09)) }
                            )
                        }
                        Row(
                            modifier = Modifier.sGap(6),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(src = AppIcons.Calendar)
                            SpanText(
                                dateStr,
                                modifier = Modifier
                                    .sFontSize(18)
                                    .fontWeight(FontWeight.Medium)
                                    .color(AppColors.Text.Secondary)
                                    .styleModifier { property("letter-spacing", s(0.09)) }
                            )
                        }
                    }

                    // Row 2: Location
                    Row(
                        modifier = Modifier.sGap(6),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(src = AppIcons.Location)
                        SpanText(
                            event.address,
                            modifier = Modifier
                                .sFontSize(18)
                                .fontWeight(FontWeight.Medium)
                                .color(AppColors.Text.Secondary)
                                .styleModifier {
                                    property("letter-spacing", s(0.09))
                                    property("white-space", "nowrap")
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                    property("max-width", s(300))
                                }
                        )
                    }
                }

                // Match details
                Column(modifier = Modifier.sGap(8)) {
                    // Row 3: Format (11 vs 11) + teams count
                    Row(
                        modifier = Modifier.sGap(8),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Row(
                            modifier = Modifier.sGap(8),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                src = AppIcons.ballSmall(event.sportName),
                                modifier = Modifier.sSize(28)
                            )
                            Row(modifier = Modifier.sGap(4)) {
                                SpanText(
                                    "$playersPerTeam",
                                    modifier = Modifier
                                        .sFontSize(18)
                                        .fontWeight(FontWeight.Medium)
                                        .color(AppColors.Text.Secondary)
                                        .styleModifier { property("letter-spacing", s(0.09)) }
                                )
                                SpanText(
                                    t.eventVs,
                                    modifier = Modifier
                                        .sFontSize(18)
                                        .fontWeight(FontWeight.Medium)
                                        .color(AppColors.Text.Secondary)
                                        .styleModifier { property("letter-spacing", s(0.09)) }
                                )
                                SpanText(
                                    "$playersPerTeam",
                                    modifier = Modifier
                                        .sFontSize(18)
                                        .fontWeight(FontWeight.Medium)
                                        .color(AppColors.Text.Secondary)
                                        .styleModifier { property("letter-spacing", s(0.09)) }
                                )
                            }
                        }

                        // Dot + teams count. Pro events only — non-Pro
                        // sessions are pickup matches without a team
                        // structure, so the count would always read "0
                        // teams" / "1 team" and just adds noise.
                        if (event.isPro) {
                            Row(
                                modifier = Modifier.sGap(4),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Box(
                                    modifier = Modifier
                                        .sSize(6)
                                        .sBorderRadius(100)
                                        .backgroundColor(AppColors.Text.Secondary)
                                )
                                SpanText(
                                    "${event.teamsCount ?: event.teams.size}",
                                    modifier = Modifier
                                        .sFontSize(18)
                                        .fontWeight(FontWeight.Medium)
                                        .color(AppColors.Text.Secondary)
                                        .styleModifier { property("letter-spacing", s(0.09)) }
                                )
                                SpanText(
                                    t.eventTeamsLabel,
                                    modifier = Modifier
                                        .sFontSize(18)
                                        .fontWeight(FontWeight.Medium)
                                        .color(AppColors.Text.Secondary)
                                        .styleModifier { property("letter-spacing", s(0.09)) }
                                )
                            }
                        }
                    }

                    // Row 4: Players count
                    Row(
                        modifier = Modifier.sGap(8),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(src = AppIcons.People)
                        Row(modifier = Modifier.sGap(4)) {
                            SpanText(
                                "$totalPlayers",
                                modifier = Modifier
                                    .sFontSize(18)
                                    .fontWeight(FontWeight.Medium)
                                    .color(AppColors.Text.Secondary)
                                    .styleModifier { property("letter-spacing", s(0.09)) }
                            )
                            SpanText(
                                t.eventPlayersLabel,
                                modifier = Modifier
                                    .sFontSize(18)
                                    .fontWeight(FontWeight.Medium)
                                    .color(AppColors.Text.Secondary)
                                    .styleModifier { property("letter-spacing", s(0.09)) }
                            )
                        }
                    }
                }
            }
        }
    }
}

/**
 * Sport-tinted PRO pill — same recipe as the mobile registration screen's
 * `MobileProBadge`: the badge's translucent blue sits on the page-background
 * composite (#02040B + blue tint), so it stays opaque and readable over any
 * photo instead of washing out like the old bare `rgba(60,121,241,0.18)`.
 */
@Composable
internal fun ProStatusBadge(sportColors: SportColorScheme) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .sBorderRadius(100)
            .sPadding(4, 10)
            .sGap(4)
            .flexShrink(0)
            .styleModifier {
                property("background-color", "#02040B")
                property(
                    "background-image",
                    "linear-gradient(rgba(60,121,241,0.18), rgba(60,121,241,0.18)), " +
                        "linear-gradient(0deg, rgba(60,121,241,0) 0%, " +
                        "rgba(60,121,241,0.072) 64%, rgba(60,121,241,0.072) 100%)",
                )
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .sSize(12)
                .backgroundColor(sportColors.primary)
                .styleModifier {
                    property("mask-image", "url('${AppIcons.Diamond}')")
                    property("-webkit-mask-image", "url('${AppIcons.Diamond}')")
                    property("mask-size", "contain")
                    property("-webkit-mask-size", "contain")
                    property("mask-position", "center")
                    property("-webkit-mask-position", "center")
                    property("mask-repeat", "no-repeat")
                    property("-webkit-mask-repeat", "no-repeat")
                }
        )
        SpanText(
            t.eventProBadge,
            modifier = Modifier
                .sFontSize(12)
                .fontWeight(FontWeight.SemiBold)
                .color(sportColors.primary)
        )
    }
}

/**
 * Completed status pill — solid green with black text, matching the Arena
 * event list's status badge (`ArenaEventCard`, `rgb(52,213,36)`), so the
 * status reads the same everywhere.
 */
@Composable
internal fun CompletedStatusBadge() {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .sBorderRadius(100)
            .sPadding(6, 12)
            .styleModifier { property("background-color", "#34D524") },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            t.eventCompletedBadge,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(FontWeight.Medium)
                .sLineHeight(18)
                .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0, 0, 0))
                .styleModifier {
                    property("letter-spacing", s(0.14))
                    property("font-family", "Urbanist, sans-serif")
                    property("white-space", "nowrap")
                }
        )
    }
}
